import React, { useRef } from "react";

import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatListBulleted from "@mui/icons-material/FormatListBulleted";
import FormatListNumbered from "@mui/icons-material/FormatListNumbered";
import FormatTextdirectionLToROutlinedIcon from "@mui/icons-material/FormatTextdirectionLToROutlined";
import FormatTextdirectionRToLOutlinedIcon from "@mui/icons-material/FormatTextdirectionRToLOutlined";
import { Box } from "@mui/material";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  LinkBubbleMenu,
  LinkBubbleMenuHandler,
  MenuButton,
  MenuButtonBold,
  MenuButtonEditLink,
  MenuButtonItalic,
  MenuButtonRemoveFormatting,
  MenuButtonUnderline,
  MenuControlsContainer,
  MenuSelectHeading,
  MenuSelectTextAlign,
  RichTextEditorProvider,
  useRichTextEditorContext
} from "mui-tiptap";
import type { TextCardConfig } from "store/types";

const editorContentSx = {
  width: "100%",
  "& .tiptap": {
    color: "#374151",
    padding: 0,
    outline: "none",
    minHeight: 60,
    fontSize: 13,
    "& p": {
      lineHeight: 1.7,
      margin: 0,
      marginBottom: "8px"
    },
    "& p:last-child": {
      marginBottom: 0
    },
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      lineHeight: 1.4,
      margin: 0,
      marginBottom: "8px"
    },
    "& h1:last-child, & h2:last-child, & h3:last-child, & h4:last-child, & h5:last-child, & h6:last-child": {
      marginBottom: 0
    },
    "& ul, & ol": {
      paddingLeft: "1.5em"
    },
    "& li": {
      marginLeft: "18px"
    },
    "& a:not([data-type='mention'])": {
      color: "#26A6AE !important",
      "&:hover": {
        textDecoration: "underline"
      }
    },
    "& p.is-editor-empty:first-of-type::before": {
      content: "attr(data-placeholder)",
      float: "left",
      height: 0,
      pointerEvents: "none",
      color: "#9eacb5"
    }
  }
};

const toolbarSx = {
  flexWrap: "wrap" as const,
  borderBottom: "1px solid #E5E7EB",
  paddingBottom: "8px",
  marginBottom: "8px",
  ".MuiSelect-select": {
    padding: "2px 20px 2px 6px !important",
    color: "#071f35"
  },
  "& .MuiButtonBase-root": {
    color: "#071f35"
  },
  "& .MuiButtonBase-root svg": {
    color: "#071f35",
    fontSize: "18px"
  }
};

const extensions = [
  StarterKit,
  Placeholder.configure({
    placeholder: "Type here...",
    showOnlyCurrent: true
  }),
  TextAlign.configure({
    types: ["heading", "paragraph"]
  }),
  LinkBubbleMenuHandler
];

const btnSx = { fill: "#071f35" };

const InlineToolbar: React.FC = () => {
  const editor = useRichTextEditorContext();

  useEditorState({
    editor,
    selector: (snapshot) => snapshot.transactionNumber
  });

  return (
    <MenuControlsContainer sx={toolbarSx}>
      <MenuButtonBold sx={btnSx} />
      <MenuButtonItalic sx={btnSx} />
      <MenuButtonUnderline sx={btnSx} />
      <MenuSelectHeading sx={btnSx} />
      <MenuSelectTextAlign
        sx={{
          ".MuiSvgIcon-root": {
            fill: "#071f35 !important",
            fontSize: "18px"
          }
        }}
        emptyLabel={
          <FormatAlignLeftIcon
            sx={{ width: "16px", height: "16px", fill: "#071f35", verticalAlign: "middle", paddingTop: "2.5px" }}
          />
        }
      />
      <MenuButton
        tooltipLabel="Ordered list"
        tooltipShortcutKeys={["mod", "Shift", "7"]}
        IconComponent={FormatListNumbered}
        selected={editor?.isActive("orderedList") ?? false}
        disabled={!editor?.isEditable}
        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        sx={btnSx}
      />
      <MenuButton
        tooltipLabel="Bulleted list"
        tooltipShortcutKeys={["mod", "Shift", "8"]}
        IconComponent={FormatListBulleted}
        selected={editor?.isActive("bulletList") ?? false}
        disabled={!editor?.isEditable}
        onClick={() => editor?.chain().focus().toggleBulletList().run()}
        sx={btnSx}
      />
      <MenuButtonEditLink />
      <MenuButton
        tooltipLabel="Left to right"
        IconComponent={FormatTextdirectionLToROutlinedIcon}
        selected={editor?.isActive({ dir: "ltr" }) ?? false}
        disabled={!editor?.isEditable}
        onClick={() => editor?.chain().focus().setTextDirection("ltr").run()}
        sx={btnSx}
      />
      <MenuButton
        tooltipLabel="Right to left"
        IconComponent={FormatTextdirectionRToLOutlinedIcon}
        selected={editor?.isEditable ? (editor?.isActive({ dir: "rtl" }) ?? false) : false}
        disabled={!editor?.isEditable}
        onClick={() => editor?.chain().focus().setTextDirection("rtl").run()}
        sx={btnSx}
      />
      <MenuButtonRemoveFormatting sx={btnSx} />
    </MenuControlsContainer>
  );
};

interface Props {
  config: TextCardConfig;
  isPreview?: boolean;
  onChange?: (content: string) => void;
}

const TextCardEditor: React.FC<Props> = ({ config, onChange }) => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    extensions,
    content: config.content,
    onUpdate: ({ editor }) => {
      onChangeRef.current?.(editor.getHTML());
    }
  });

  if (!editor) return null;

  return (
    <Box
      sx={{ p: "12px 16px", cursor: "text" }}
      onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
      onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
    >
      <RichTextEditorProvider editor={editor}>
        <InlineToolbar />
        <Box sx={editorContentSx}>
          <EditorContent editor={editor} />
        </Box>
        <LinkBubbleMenu />
      </RichTextEditorProvider>
    </Box>
  );
};

const TextCardPreview: React.FC<{ content: string }> = ({ content }) => {
  const editor = useEditor({
    extensions,
    content,
    editable: false
  });

  if (!editor) return null;

  return (
    <Box sx={{ p: "20px 24px", ...editorContentSx }}>
      <EditorContent editor={editor} />
    </Box>
  );
};

export const TextCard: React.FC<Props> = (props) => {
  if (props.isPreview) {
    return <TextCardPreview content={props.config.content} />;
  }
  return <TextCardEditor {...props} />;
};
